import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate } from 'react-router-dom';
import { Paperclip } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { Card } from '@/components/ui/Card/Card';
import { Select } from '@/components/ui/Select/Select';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { adminFeedbackService } from '@/services/adminFeedbackService';
import { knowledgeService } from '@/services/knowledgeService';
import { useNotificationStore } from '@/stores/notificationStore';

const statusOptions = ['open', 'in_progress', 'resolved', 'closed'] as const;

const roleLabels: Record<string, string> = { user: 'User', admin: 'Admin', ai: 'AI' };
const roleVariants: Record<string, 'default' | 'success' | 'secondary'> = {
  user: 'default',
  admin: 'success',
  ai: 'secondary',
};
const typeVariants: Record<string, 'error' | 'default' | 'success'> = {
  bug: 'error',
  feature: 'default',
  help: 'success',
};

export function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotificationStore();
  const [ticket, setTicket] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (id) loadTicket();
  }, [id]);

  const loadTicket = async () => {
    setIsLoading(true);
    try {
      const result = await adminFeedbackService.getTicket(id!);
      setTicket(result.ticket);
      setMessages(result.messages);
      setStatus(result.ticket.status);
    } catch (err) {
      console.error('Failed to load ticket:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !id) return;
    setIsSending(true);
    try {
      await adminFeedbackService.reply(id, replyText);
      setReplyText('');
      await loadTicket();
      notify.success('Reply sent and email dispatched to user');
    } catch (err: any) {
      notify.error(err.message || 'Failed to send reply');
    } finally {
      setIsSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    try {
      await adminFeedbackService.updateStatus(id, newStatus);
      setStatus(newStatus);
      notify.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (err: any) {
      notify.error(err.message || 'Failed to update status');
    }
  };

  const handleCreateArticle = async () => {
    if (!id) return;
    try {
      const article = await knowledgeService.createFromTicket(id);
      notify.success('Knowledge article created as draft');
      navigate(`/admin/knowledge/${article.id}`);
    } catch (err: any) {
      notify.error(err.message || 'Failed to create article');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (isLoading) return <PageContainer className="text-center"><span className="text-muted-foreground">Loading...</span></PageContainer>;
  if (!ticket) return <PageContainer className="text-center"><span className="text-muted-foreground">Ticket not found</span></PageContainer>;

  return (
    <PageContainer>
      <PageHeader
        title={ticket.subject || '(no subject)'}
        description={`Submitted ${formatDate(ticket.created_at)} by ${ticket.user_email}`}
      >
        <div className="flex items-center gap-2">
          {ticket.type === 'help' && (
            <Button variant="outline" size="sm" onClick={handleCreateArticle}>
              Add to Knowledge Base
            </Button>
          )}
          <Select
            value={status}
            onValueChange={handleStatusChange}
            options={statusOptions.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
            className="h-8 w-[140px]"
          />
        </div>
      </PageHeader>

      {/* Type & Status badges */}
      <div className="flex items-center gap-2 mb-6">
        <Badge variant={typeVariants[ticket.type] || 'secondary'}>{ticket.type}</Badge>
        <Badge variant="secondary">{status.replace('_', ' ')}</Badge>
        {ticket.org_name && <span className="text-sm text-muted-foreground">{ticket.org_name}</span>}
      </div>

      {/* Metadata */}
      {ticket.metadata && Object.keys(ticket.metadata).length > 0 && (
        <Card variant="default" padding="sm" className="mb-6 text-xs text-muted-foreground space-y-1">
          {ticket.metadata.pageUrl && <div><strong>Page:</strong> {ticket.metadata.pageUrl}</div>}
          {ticket.metadata.userAgent && <div><strong>Browser:</strong> {ticket.metadata.userAgent.slice(0, 120)}</div>}
          {ticket.metadata.screenWidth && <div><strong>Screen:</strong> {ticket.metadata.screenWidth}x{ticket.metadata.screenHeight}</div>}
        </Card>
      )}

      {/* Messages */}
      <div className="space-y-3 mb-6">
        {messages.map((msg: any) => (
          <Card key={msg.id} variant="bordered" padding="sm">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={roleVariants[msg.role] || 'secondary'}>
                {roleLabels[msg.role] || msg.role}
              </Badge>
              <span className="text-xs text-muted-foreground">{formatDate(msg.created_at)}</span>
            </div>
            {msg.role === 'ai' ? (
              <div className="text-sm text-foreground chat-markdown">
                <ReactMarkdown>{msg.body}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-foreground whitespace-pre-wrap">{msg.body}</p>
            )}
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {msg.attachments.map((att: any, i: number) => (
                  <a
                    key={i}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-muted border border-input rounded-md text-xs text-foreground hover:bg-muted/80"
                  >
                    <Paperclip className="w-3 h-3" />
                    {att.filename}
                  </a>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Reply Form */}
      <div className="border-t border-input pt-6">
        <Textarea
          label="Reply to user (sends email)"
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Type your reply..."
          rows={4}
        />
        <div className="flex justify-end mt-3">
          <Button
            variant="primary"
            size="sm"
            onClick={handleReply}
            disabled={isSending || !replyText.trim()}
            isLoading={isSending}
          >
            Send Reply
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
